/**
 * QC review tasks (1.0.0-rc.9): whom a failed run's review task goes to (a role, an access group or a person), when it
 * is due, and whether a run with warnings makes one too. The general setting (Administration → Quality control) and a
 * watch folder's own choice use these fields.
 */
import type { QcReviewTask, QcTaskAssignee } from '@prism/contracts';
import { useAssignees } from '../api/hooks';

/** "role:media_manager", "group:<id>", "user:<id>" ↔ an assignee. */
export const qcAssigneeValue = (a: QcTaskAssignee | null | undefined) => (!a ? '' : a.type === 'role' ? `role:${a.key}` : `${a.type}:${a.id}`);
export function qcAssigneeOf(v: string): QcTaskAssignee | null {
  if (v.startsWith('role:')) return { type: 'role', key: v.slice(5) };
  if (v.startsWith('group:')) return { type: 'group', id: v.slice(6) };
  if (v.startsWith('user:')) return { type: 'user', id: v.slice(5) };
  return null;
}

/** The review task fields; `value` null is "no task". `offLabel`: the first choice's label. */
export function QcReviewTaskFields({ value, onChange, idPrefix, offLabel = 'No review task' }: {
  value: QcReviewTask | null; onChange: (v: QcReviewTask | null) => void; idPrefix: string; offLabel?: string;
}) {
  const a = useAssignees().data;
  const set = (patch: Partial<QcReviewTask>) => value && onChange({ ...value, ...patch });
  return (
    <div className="qc-review-task" data-testid={`${idPrefix}-review-task`}>
      <select id={`${idPrefix}-assignee`} className="select" aria-label="Create a review task for" value={qcAssigneeValue(value?.assignee)}
        onChange={(e) => {
          const assignee = qcAssigneeOf(e.target.value);
          onChange(assignee ? { assignee, dueHours: value?.dueHours ?? null, onWarnings: value?.onWarnings ?? false } : null);
        }} data-testid={`${idPrefix}-task-assignee`}>
        <option value="">{offLabel}</option>
        {a && a.roles.length > 0 && <optgroup label="Roles">{a.roles.map((r) => <option key={r.key} value={`role:${r.key}`}>{r.name} ({r.members})</option>)}</optgroup>}
        {a && a.groups.length > 0 && <optgroup label="Access groups">{a.groups.map((g) => <option key={g.id} value={`group:${g.id}`}>{g.name} ({g.members})</option>)}</optgroup>}
        {a && <optgroup label="People">{a.people.map((p) => <option key={p.id} value={`user:${p.id}`}>{p.name}</option>)}</optgroup>}
      </select>
      {value && (
        <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap', marginTop: 6 }}>
          <label className="toggle-row" style={{ gap: 6 }}>Due after
            <input className="input" type="number" min={1} max={2160} style={{ width: 80 }} value={value.dueHours ?? ''} placeholder="—"
              onChange={(e) => set({ dueHours: e.target.value ? Math.max(1, Math.round(Number(e.target.value))) : null })} aria-label="Due after (hours)" data-testid={`${idPrefix}-task-due`} /> hours</label>
          <label className="toggle-row"><input type="checkbox" checked={value.onWarnings} onChange={(e) => set({ onWarnings: e.target.checked })} data-testid={`${idPrefix}-task-warnings`} /> Also when a run has warnings</label>
        </div>
      )}
    </div>
  );
}
